import dayjs, { Dayjs } from 'dayjs';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ApproveModal from './modals/approve-modal.component';
import { fakeTimesheets } from 'features/pending-timesheets/test-data/fake-timesheets';
import {
    formatPayPeriod,
    PendingTimesheet,
    TimesheetList,
} from 'features/pending-timesheets/models/pending-timesheets.model';
import { IApprovedTimesheet } from 'features/pending-timesheets/models/approved-timesheet.model';
import { PENDING_TIMESHEETS } from 'api/use-query-constants';
import renderDesktop from './pending-timesheets.desktop';
import renderMobile from './pending-timesheets.mobile';
import { RootState } from 'store/root-reducer';
import {
    setChangedPendingTimesheets,
    setPendingTimesheets,
    toggleSelectAll,
    toggleSelection,
} from 'features/pending-timesheets/redux/pending-timesheets-slice';
import { setDisablingSpinnerVisible } from 'redux/global-elements-slice';
import SmartComponent from 'utils/smart-component';
import { TypedLocalState } from 'utils/redux-hooks';
import { useApproveTimesheetsMutation } from 'features/pending-timesheets/hooks/approve-timesheets-mutation.hook';
import useTimesheets from 'features/pending-timesheets/hooks/pending-timesheets.hook';
import 'features/pending-timesheets/styles/pending-timesheets.scss';
import { AppConfig } from '../../config/config.model';
import { openModal } from 'redux/message-modal-slice';
import { getQueryClient } from '../../config/config';

export interface IPendingTimesheetProps {
    facilityId: string;
    payCycleDate: string;
    url: string;
    hospitalName: string;
    payPeriodStart: Dayjs;
    payPeriodEnd: Dayjs;
    userRequestedMockData: boolean;
}

export interface IPendingTimesheetState {
    approvalModal: JSX.Element;
    approve: (
        approvedTimesheets: IApprovedTimesheet[],
        supervisorSignature: string,
        supervisorName: string,
    ) => Promise<void>;
    currentHeaderHeight?: number;
    error: { message?: string };
    hospitalName?: string;
    isApproveDisabled: boolean;
    isLoading: boolean;
    payPeriod: string;
    payPeriodStart?: Dayjs;
    payPeriodEnd?: Dayjs;
    selectAll: boolean;
    selectedTimesheetCount?: number;
    setShowApproveModal: (showApproveModal: boolean) => void;
    showApproveModal: boolean;
    SHOW_MOCK_DATA: boolean;
    timesheets: PendingTimesheet[];
    toggleSelectAllRows: () => void;
    headerEl?: MutableRefObject<HTMLDivElement>;
    contentEl?: MutableRefObject<HTMLDivElement>;
    footerEl?: MutableRefObject<HTMLElement>;
    userRequestedMockData: boolean;
}

const useInitialState = ({
    url,
    hospitalName,
    payPeriodStart,
    payPeriodEnd,
    userRequestedMockData,
}: IPendingTimesheetProps): IPendingTimesheetState => {
    const config = useSelector((state: RootState) => state.globalElements.appConfig as AppConfig);
    const { error, isLoading, data: timesheets = new Array<PendingTimesheet>() } = useTimesheets(
        url,
        config.TIMESHEET_SERVICE_BASE_URL,
    );

    const dispatch = useDispatch();
    const [showApproveModal, setShowApproveModal]: TypedLocalState<boolean> = React.useState(false);
    const { mutate: ApproveTimesheetsMutation } = useApproveTimesheetsMutation(url, () => {
        setShowApproveModal(false);
    });

    const updatedTimeheets = useSelector(
        (state: RootState) => state.pendingTimesheets.changedTimesheets as PendingTimesheet[],
    );

    const pendingTimesheets = useSelector(
        (state: RootState) => state.pendingTimesheets.timesheets as PendingTimesheet[],
    );

    const selectAll = useSelector((state: RootState) => state.pendingTimesheets.selectAll);

    const selectedTimesheetCount = useSelector((state: RootState) => state.pendingTimesheets?.selectedTimesheetCount);

    const { showMockData } = useSelector((state: RootState) => state.pendingTimesheets);

    const toggleSelectAllRows = () => {
        dispatch(toggleSelectAll());
    };

    const isApproveDisabled = selectedTimesheetCount < 1;

    const deselectAllRows = () => {
        for (const timesheet of pendingTimesheets) {
            dispatch(toggleSelection({ timesheet, isSelected: false }));
        }
    };

    const approve = async (
        approvedTimesheets: IApprovedTimesheet[],
        supervisorSignature: string,
        supervisorName: string,
    ) => {
        ApproveTimesheetsMutation({
            approvedTimesheets,
            updatedTimeheets,
            timesheets: pendingTimesheets,
            supervisorSignature,
            supervisorName,
        });

        dispatch(setDisablingSpinnerVisible(true));
        deselectAllRows();
    };

    const payPeriod =
        typeof payPeriodStart === 'string' &&
        typeof payPeriodEnd === 'string' &&
        dayjs(formatPayPeriod(payPeriodStart)).isValid()
            ? `${formatPayPeriod(payPeriodStart)} - ${formatPayPeriod(payPeriodEnd)}`
            : '';

    React.useEffect(() => {
        if (isLoading === false) {
            const fallback = config.SHOW_MOCK_DATA && userRequestedMockData ? fakeTimesheets : [];
            const timesheetsValue = timesheets?.length < 1 ? fallback : timesheets;
            const timesheetMap = new TimesheetList(timesheetsValue).sortByDate().asMap();
            const sortedTimesheets: PendingTimesheet[] = [];

            Object.keys(timesheetMap)
                .sort()
                .reverse()
                .forEach((key: string) => {
                    sortedTimesheets.push(...new TimesheetList(timesheetMap[key]).sort());
                });

            getQueryClient().setQueryData(PENDING_TIMESHEETS, sortedTimesheets);
            dispatch(setPendingTimesheets(sortedTimesheets));
            dispatch(setChangedPendingTimesheets(sortedTimesheets));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, config]);

    const headerEl = useRef(null);
    const contentEl = useRef(null);
    const footerEl = useRef(null);
    const headerHeight = headerEl.current?.offsetHeight;
    const contentHeight = contentEl.current?.offsetHeight;
    const footerHeight = footerEl.current?.offsetHeight;
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    useEffect(() => {
        function handleResize() {
            setWindowHeight(window.innerHeight);
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (headerHeight + contentHeight + footerHeight > windowHeight) {
            footerEl.current?.classList.add('footer--shadow');
        } else {
            footerEl.current?.classList.remove('footer--shadow');
        }
    }, [headerHeight, contentHeight, footerHeight, windowHeight]);

    const [currentHeaderHeight, setCurrentHeaderHeight]: TypedLocalState<number> = useState(0);

    useEffect(() => {
        const headerHeight =
            (document.getElementsByClassName('tt-header') as HTMLCollection)?.item(0)?.getBoundingClientRect().height ||
            0;

        if (headerHeight && headerHeight !== currentHeaderHeight) {
            setCurrentHeaderHeight(headerHeight);
        }
    }, [currentHeaderHeight, timesheets]);

    useEffect(() => {
        if (error && !showMockData) {
            dispatch(
                openModal({
                    title: 'Error loading timesheet',
                    body: 'Please contact Medical Solutions',
                }),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    const approvalModal = (
        <ApproveModal
            isOpen={showApproveModal}
            close={() => {
                setShowApproveModal(false);
                deselectAllRows();
            }}
            approve={approve}
            selectedTimesheetCount={selectedTimesheetCount}
            selectedTimesheetIds={pendingTimesheets
                .filter((timesheet: PendingTimesheet) => timesheet.isSelected === true)
                .map((timesheet: PendingTimesheet) => {
                    return timesheet.timesheetId;
                })}
            confirmationEmail={'name@domain.com'}
        />
    );

    return {
        approvalModal,
        approve,
        currentHeaderHeight,
        error,
        hospitalName,
        isApproveDisabled,
        isLoading,
        payPeriod,
        payPeriodStart,
        payPeriodEnd,
        selectAll,
        selectedTimesheetCount,
        setShowApproveModal,
        showApproveModal,
        timesheets: pendingTimesheets,
        SHOW_MOCK_DATA: (config || {}).SHOW_MOCK_DATA,
        toggleSelectAllRows,
        headerEl,
        contentEl,
        footerEl,
        userRequestedMockData,
    };
};

export { renderDesktop as PendingTimesheetsDesktop, renderMobile as PendingTimesheetsMobile };

export default SmartComponent<IPendingTimesheetProps, IPendingTimesheetState>(
    useInitialState,
    renderDesktop,
    renderMobile,
);
